import React, { FC, useCallback, useEffect, useState } from "react";

import { OverlayTrigger, Table, Tooltip } from "react-bootstrap";

import classNames from "classnames";
import currency from "currency.js";
import { isNil } from "lodash";

import { PaymentLineDetailsWithResaleDetails, PaymentLineStatuses, SortDirection } from "@api/graphql";
import { formatISODateToFormat, sortData } from "@util/index";

import { RoyaltyStatusBadge } from "./RoyaltyStatusBadge";
import { TableContents, TableHeader } from "./TableContents";
import { TableHeaders as CustomTableHeaders } from "./TableHeaders";

import styles from "./RoyaltiesTable.module.scss";

interface PaymentLineDetailsToDisplay extends Omit<PaymentLineDetailsWithResaleDetails, "resale"> {}

const DEFAULT_TABLE_HEADERS: TableHeader<PaymentLineDetailsToDisplay>[] = [
  { label: "ID", accessor: "resale_id" },
  { label: "Artwork title", accessor: "artwork_name" },
  { label: "Artist", accessor: "artist_name" },
  { label: "Sold date", accessor: "artwork_sale_date" },
  { label: "Resale price", accessor: "artwork_sale_price" },
  { label: "Payment", accessor: "royalty_net" },
  { label: "Status", accessor: "status" },
];

interface RoyaltiesTableProps {
  paymentLines: PaymentLineDetailsWithResaleDetails[];
  onRowClick?: (rowData: PaymentLineDetailsWithResaleDetails) => void;
  customTableHeaders?: TableHeader<PaymentLineDetailsToDisplay>[];
  customTableStyleClassName?: string;
}

const WithTooltip = ({ children, label }: { children: React.ReactElement; label: string }) => {
  return (
    <OverlayTrigger placement="top-start" overlay={<Tooltip>{label}</Tooltip>}>
      {children}
    </OverlayTrigger>
  );
};

const DefaultTableCellComponent = ({
  accessor,
  value,
}: {
  accessor: keyof PaymentLineDetailsToDisplay;
  value: PaymentLineDetailsToDisplay[keyof PaymentLineDetailsToDisplay];
}) => {
  return (
    <td className="align-content-center" key={String(accessor)}>
      <span>{value}</span>
    </td>
  );
};

const DateComponent = ({ accessor, value }: { accessor: keyof PaymentLineDetailsWithResaleDetails; value: string }) => (
  <td className="align-content-center" key={String(accessor)}>
    <span>{formatISODateToFormat({ isoDate: value, targetDateFormat: "DD/MM/YYYY" })}</span>
  </td>
);

const CurrencyComponent = ({
  accessor,
  value,
}: {
  accessor: keyof PaymentLineDetailsWithResaleDetails;
  value: string;
}) => (
  <td className="align-content-center" key={String(accessor)}>
    <span>{currency(value).format()}</span>
  </td>
);

const TooltipComponent = ({
  accessor,
  value,
}: {
  accessor: keyof PaymentLineDetailsWithResaleDetails;
  value: string;
}) => (
  <td className="align-content-center" key={String(accessor)}>
    <WithTooltip label={value}>
      <span>{value}</span>
    </WithTooltip>
  </td>
);

const RoyaltyStatusBadgeComponent = ({
  accessor,
  value,
}: {
  accessor: keyof PaymentLineDetailsWithResaleDetails;
  value: PaymentLineStatuses;
}) => {
  const cellValue = value ?? undefined;
  return (
    <td key={String(accessor)}>
      <div className={classNames(styles["badge-cell"], styles["badge-truncate"], "d-flex align-content-center")}>
        <RoyaltyStatusBadge cellValue={cellValue as PaymentLineStatuses | undefined} />
      </div>
    </td>
  );
};

export const RoyaltiesTable: FC<RoyaltiesTableProps> = ({
  paymentLines,
  onRowClick,
  customTableHeaders,
  customTableStyleClassName,
}) => {
  const [sortKey, setSortKey] = useState<keyof PaymentLineDetailsWithResaleDetails>("id");
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASCENDING);
  const [sortedData, setSortedData] = useState(paymentLines);

  const mapColumnValue = useCallback(
    (value: PaymentLineDetailsToDisplay[keyof PaymentLineDetailsToDisplay], key: keyof PaymentLineDetailsToDisplay) => {
      // convert nullish value to "", Boolean("") => false
      const text = isNil(value) ? "" : value;

      // default component
      let component = undefined;

      switch (key) {
        case "artwork_sale_date":
          component = <DateComponent accessor={key} value={text as string} />; // artwork_sale_date type is string
          break;
        case "artwork_name":
        case "artist_name":
          component = <TooltipComponent accessor={key} value={text as string} />; // artwork_name/artist_name type is string
          break;
        case "status":
          component = <RoyaltyStatusBadgeComponent accessor={key} value={value as PaymentLineStatuses} />; // it won't be null or undefined, status type is string
          break;
        case "artwork_sale_price":
        case "royalty_net":
          component = <CurrencyComponent accessor={key} value={value as string} />;
          break;
        default:
          component = <DefaultTableCellComponent accessor={key} value={text} />;
      }

      return { text, component };
    },
    [],
  );

  // Effect only applies to when the resale is updated
  // apply the current sortKey and current sortDirection to the updated resale data
  useEffect(() => {
    setSortedData(sortData([...paymentLines], sortDirection, sortKey));
  }, [paymentLines, sortDirection, sortKey]);

  const sortByKey = useCallback(
    (key: keyof PaymentLineDetailsWithResaleDetails) => {
      let direction: SortDirection = SortDirection.ASCENDING;
      if (key === sortKey) {
        // Toggle sorting direction if the same column is clicked
        direction = sortDirection === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;
      } else {
        // Default to ascending when a new column is clicked
        direction = SortDirection.ASCENDING;
      }

      const sortedData = sortData([...paymentLines], direction, key);

      setSortedData(sortedData);
      setSortKey(key);
      setSortDirection(direction);
    },
    [paymentLines, sortDirection, sortKey],
  );

  return (
    <>
      <Table borderless hover className={customTableStyleClassName}>
        <CustomTableHeaders
          headers={customTableHeaders ?? DEFAULT_TABLE_HEADERS}
          customStyles={styles}
          sortColumn={sortKey}
          setSortColumn={sortByKey}
          sortDirection={sortDirection.toLowerCase() as "ascending" | "descending"}
        />

        <TableContents<PaymentLineDetailsToDisplay>
          headers={customTableHeaders ?? DEFAULT_TABLE_HEADERS}
          data={sortedData}
          keyAccessor="id"
          customStyles={styles}
          mapColumnValue={mapColumnValue}
          onRowClick={onRowClick}
        />
      </Table>
    </>
  );
};
